// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, useEffect, useRef } from 'react'
import { formatDob } from '../../api/format'
import { Search, Check, Loader2, X } from 'lucide-react'
import * as api from '../../api/client'

type Kind = 'patient' | 'prescriber'
// D-CRX-29 T10: a pick may carry the therapy it came from. Queue rows always do (one row per
// therapy); a free-text patient search cannot, so it stays optional and the server's therapy_id_required
// 409 remains the honest answer for an ambiguous pick — now rendered as mapped text, not a raw string.
export interface PickedEntity { id: string; label: string; sub?: string; therapyId?: string; drugName?: string }

/**
 * Search-as-you-type picker over OwlRx (PioneerRx-sourced) patients or prescribers.
 * Uses the existing /patients?q= and /prescribers?q= search endpoints.
 */
export default function EntityPicker({ kind, value, onPick }: {
  kind: Kind
  value: PickedEntity | null
  onPick: (e: PickedEntity | null) => void
}) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [results, setResults] = useState<PickedEntity[]>([])
  const [loading, setLoading] = useState(false)
  const timer = useRef<any>(null)

  useEffect(() => {
    if (!open) return
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      setLoading(true)
      try {
        if (kind === 'patient') {
          const d = await api.listPatients(q || undefined)
          setResults(d.items.slice(0, 12).map(p => ({ id: p.id, label: p.display_name, sub: p.date_of_birth ? `DOB ${formatDob(p.date_of_birth)}` : undefined })))
        } else {
          const d = await api.listPrescribers(q || undefined)
          setResults(d.items.slice(0, 12).map(p => ({ id: p.id, label: p.display_name, sub: `NPI ${p.npi}${p.specialty ? ' · ' + p.specialty : ''}` })))
        }
      } catch { setResults([]) } finally { setLoading(false) }
    }, 250)
    return () => timer.current && clearTimeout(timer.current)
  }, [q, open, kind])

  if (value) {
    return (
      <div style={chip}>
        <Check size={14} style={{ color: 'var(--color-primary)' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{value.label}</div>
          {value.sub && <div style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{value.sub}</div>}
        </div>
        <button type="button" style={iconBtn} onClick={() => onPick(null)} aria-label="Clear"><X size={14} /></button>
      </div>
    )
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={searchWrap}>
        <Search size={15} style={{ color: 'var(--color-text-mute)' }} />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={kind === 'patient' ? 'Search patients by name…' : 'Search prescribers by name or NPI…'}
          style={{ border: 'none', outline: 'none', flex: 1, fontSize: 13, background: 'transparent', color: 'var(--color-text)' }}
        />
        {loading && <Loader2 className="spin" size={14} />}
      </div>
      {open && results.length > 0 && (
        <div style={dropdown}>
          {results.map(r => (
            <button
              key={r.id}
              type="button"
              style={option}
              // mouseDown (not click) + preventDefault: fire the selection BEFORE the
              // input blurs or any parent <label> forwards the click to its control.
              onMouseDown={(e) => { e.preventDefault(); onPick(r); setOpen(false); setQ('') }}
            >
              <div style={{ fontSize: 13, fontWeight: 500 }}>{r.label}</div>
              {r.sub && <div style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{r.sub}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const searchWrap: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)' }
const dropdown: React.CSSProperties = { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, boxShadow: '0 4px 14px rgba(0,0,0,0.12)', zIndex: 20, maxHeight: 280, overflowY: 'auto' }
const option: React.CSSProperties = { display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', color: 'var(--color-text)' }
const chip: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid var(--color-primary)', borderRadius: 6, background: 'var(--color-surface-alt)' }
const iconBtn: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-mute)' }
